<template>
  <el-card
    v-loading="isloading"
    style="border: none"
    shadow="never"
    :body-style="{ padding: '10px' }"
  >
    <div slot="header" class="firstlog-chart-header">
      <span style="font-size: 14px; font-weight: bold">近三十天访问记录</span>
    </div>

    <div ref="container" class="firstlog-chart"></div>
  </el-card>
</template>

<script>
import * as echarts from 'echarts/core'

import request from '@/mixins/request.js'

import dayjs from 'dayjs'

import { throttle } from 'lodash'

export default {
  mixins: [request],

  data() {
    const elementContainer = {}

    const createTooltip = () => {
      const scopeId = this.$options._scopeId

      const div = document.createElement('div')
      div.className = 'chart-tooltip'

      const span0 = document.createElement('span')
      span0.className = 'span0'
      const span1 = document.createElement('span')
      span1.className = 'span1'
      const span3 = document.createElement('span')
      span3.className = 'span3'

      div.append(span0, span1, span3)

      if (scopeId) {
        div.setAttribute(scopeId, '')
        span0.setAttribute(scopeId, '')
        span1.setAttribute(scopeId, '')
        span3.setAttribute(scopeId, '')
      }

      return [div, span0, span1, span3]
    }

    this.updateTooltop = (key, color, count) => {
      const [div, span0, span1, span3] =
        elementContainer[key] || (elementContainer[key] = createTooltip())

      span1.textContent = key

      span3.textContent = count

      span0.style.backgroundColor = color

      return div
    }

    return {
      isloading: false,
    }
  },

  watch: {
    '$store.state.app.sidebar.opened'() {
      if (this.chart) {
        setTimeout(() => this.chart.resize(), 600)
      }
    },
  },

  mounted() {
    this.resize = () => throttle(this.chart && this.chart.resize(), 600)

    this.$nextTick(async () => {
      this.chart = echarts.init(this.$refs.container)

      this.chart.on('click', event => {
        console.log(event)

        let xaxis, service

        if (event.componentType === 'xAxis') {
          xaxis = this.chart
            .getOption()
            .xAxis[0].data.find(it => it.value === event.value)
        } else {
          xaxis = this.chart
            .getOption()
            .xAxis[0].data.find(it => it.value === event.name)
          if (event.componentSubType === 'bar') {
            service = event.seriesName
          }
        }

        const time = dayjs(xaxis.time * 1000)

        const etm = time.add(1, 'd').format('YYYY-MM-DD'),
          btm = time.format('YYYY-MM-DD')

        this.$emit('update:service', service)
        this.$emit('update:btm', btm)
        this.$emit('update:etm', etm)
      })

      const option = await this.loadData()

      if (option && this.chart) {
        this.chart.setOption(option)
      }

      this.$bus.$on('resize', this.resize)

      this.$on('destroyed', this.$bus.$off('resize', this.resize))
    })
  },

  destroyed() {
    if (this.chart) {
      this.chart.dispose()
      this.chart = null
    }
    this.$off('destroyed')
  },

  methods: {
    async loadData() {
      this.isloading = true
      try {
        const updateTooltop = this.updateTooltop

        const option = {
          tooltip: {
            trigger: 'axis',
            axisPointer: {
              type: 'cross',
              crossStyle: {
                color: '#999',
              },
            },
            formatter: params => {
              let elements = []
              for (const item of params) {
                elements.push(
                  updateTooltop(
                    item.seriesName,
                    item.color,
                    item.value,
                    (item.data || {}).successfulCount
                  )
                )
              }

              return elements
            },
          },
          dataZoom: [
            {
              type: 'slider',
              start: 90,
              end: 100,
            },
          ],
          xAxis: [
            {
              type: 'category',
              nameLocation: 'center',
              data: [],
              axisPointer: {
                type: 'shadow',
              },
              triggerEvent: true,
            },
          ],
          yAxis: [
            {
              type: 'value',
            },
          ],
          series: [],
          legend: {
            data: [],
          },
        }

        const resp = await this.$api.get('/schedules/apicallogs/firstlog/chart')

        if (resp.data.status === 1) {
          let list = resp.data.content || [],
            metricsLabels = [],
            i,
            j

          for (const item of list) {
            const time = dayjs(item.time * 1000)

            item.label = time.format('MM/DD')

            const metrics = item.metrics || []

            for (const metric of metrics) {
              if (!metricsLabels.includes(metric.service)) {
                metricsLabels.push(metric.service)
              }
            }

            j = Math.max(j || 0, metrics.length)

            i = Math.min(i || Number.MAX_VALUE, metrics.length)
          }

          if (i !== j) {
            for (const item of list) {
              const metrics = item.metrics || (item.metrics = [])

              for (const label of metricsLabels) {
                if (!metrics.find(metric => metric.service === label)) {
                  metrics.push({
                    service: label,
                    count: 0,
                    successfulCount: 0,
                  })
                }
              }

              metrics.sort(
                (a, b) =>
                  metricsLabels.indexOf(a.service) -
                  metricsLabels.indexOf(b.service)
              )
            }
          }

          for (const item of list) {
            option.xAxis[0].data.push({
              time: item.time,
              value: item.label,
            })

            let i = 0,
              j = metricsLabels.length
            for (; i < j; i++) {
              const chunk =
                option.series[i] ||
                (option.series[i] = {
                  type: 'bar',
                  name: metricsLabels[i],
                  data: [],
                  data2: [],
                })
              chunk.data.push({
                value: item.metrics[i].count,
                successfulCount: item.metrics[i].successfulCount,
              })
              chunk.data2.push(item.metrics[i].successfulCount || 0)
            }

            const chunk =
              option.series[i] ||
              (option.series[i] = {
                type: 'line',
                name: 'Count',
                smooth: true,
                lineStyle: {
                  width: 1,
                },
                data: [],
              })

            chunk.data.push(item.count || 0)
          }

          option.legend.data = [...metricsLabels, 'Count']
          return option
        } else {
          this.$message.error(resp.data.message || '加载数据错误')
        }
      } catch (e) {
        console.error(e)
      } finally {
        this.isloading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.firstlog-chart {
  height: 400px;
}

.chart-tooltip {
  font-size: 12px;

  .span0 {
    display: inline-block;
    width: 0.8em;
    height: 0.8em;
    margin-right: 5px;
    border-radius: 50%;
  }

  .span1 {
    display: inline-block;
    width: 130px;
  }
}

.firstlog-chart-header {
  display: flex;
  flex-direction: row;
  height: 30px;
  align-items: center;
}
</style>
